import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { apiRoot } from '../config'
import { RootState } from './index'
import { Extractor, getQueryLinks, hasDefinitions } from '../Utilities/queryLinks'
import { ResponseStatus, SearchState } from '../Types/SearchTypes'

interface SearchParams {
  source: string;
  target: string;
  query: string;
}

// Define the async thunk
export const performSearch = createAsyncThunk(
  'search/fetchSearchResults',
  async ({ source, target, query }: SearchParams, { dispatch, getState }) => {
    const rootState = getState() as RootState
    const extractors = (rootState.search.results
        .filter(r => !r.extractor.linkOnly)
        .map(r => r.extractor)
    )
    if (query) {
      await Promise.all(
        extractors.map((extractor) =>
          dispatch(searchOneProvider({ source, target, query, extractor })),
        ),
      )
    }
    return { source, target, query, extractors }
  })

interface SearchOneProviderParams {
  source: string;
  target: string;
  query: string;
  extractor: Extractor;
}

const searchOneProvider = createAsyncThunk(
  'search/fetchOneResult',
  async ({ source, target, query, extractor }: SearchOneProviderParams) => {

    // // Dummy results
    // console.log('TODO getOneResult', source, target, query, provider)
    // return { source, target, query, provider, results: `TODO ${provider}: ${query} (${source}->${target})` }

    const searchParams = new URLSearchParams({
      source: extractor.category === 'definitions-target' ? target : source,
      target: extractor.category === 'definitions-source' ? source : target,
      query,
      extractorName: extractor.name,
      groupName: {
        dictionaries: 'normal',
        'definitions-source': 'definitions',
        'definitions-target': 'definitions',
      }[extractor.category],
      operation: 'get_one',
    })
    const response = await axios.get(
      apiRoot + `/search-api/?${searchParams.toString()}`)
    return {
      extractorName: extractor.name,
      text: response.data.text,
      data: response.data.data,
      status: (response.data.success ? 'succeeded' : 'failed') as ResponseStatus,
    }
  },
)

// Define the slice
const searchSlice = createSlice({
  name: 'search',
  initialState: {
    currentSearch: null,
    activeTab: 'dictionaries',
    results: [],
    status: 'idle',
    error: null,
  } as SearchState,
  reducers: {
    setActiveTab(state, action) {
      state.activeTab = action.payload
    },
    updateQueryLinks(state, action) {
      const { source, target, query } = action.payload
      // Update the query links for linkOnly extractors
      const providers = getQueryLinks(source, target, query)
      console.log('updateQueryLinks', source, target, query, providers)
      state.results = providers.map(extractor => {
        if (extractor.linkOnly) {
          return {
            extractor,
            response: { status: extractor.linkOnly ? 'succeeded' : 'idle' },
          }
        }

        const found = state.results.find(r => r.extractor.name === extractor.name)

        // Return the existing result with updated extractor (in case the link changed)
        return {
          extractor,
          response: found?.response || { status: 'idle' },
        }
      })
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(performSearch.pending, (state, action) => {
        // sets up the search
        console.log('performSearch.pending', action)
        const { source, target, query } = action.meta.arg
        state.currentSearch = { source, target, query }
        if (!hasDefinitions(source)) {
          state.activeTab = 'dictionaries'
        }
        const providers = getQueryLinks(source, target, query)
        state.results = providers.map(extractor => ({
          extractor,
          response: { status: extractor.linkOnly ? 'succeeded' : 'loading' },
        }))
        state.status = 'loading'
      })
      .addCase(performSearch.fulfilled, (state, action) => {
        console.log('performSearch.fulfilled', action)
        state.results.forEach((result) => {
          if (result.response.status === 'loading') {
            result.response.status = 'succeeded'
          }
        })
        state.status = 'succeeded'
      })
      .addCase(performSearch.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message || ''
      })
      .addCase(searchOneProvider.fulfilled, (state, action) => {
        const { extractorName, text, data } = action.payload
        const ix = state.results.findIndex(r => r.extractor.name === extractorName)
        state.results = [...state.results]
        state.results[ix] = {
          extractor: state.results[ix].extractor,
          response: { text, data, status: 'succeeded' },
        }
      })
      .addCase(searchOneProvider.rejected, (state, action) => {
        const ix = state.results.findIndex(r => r.extractor.name === action.meta.arg.extractor.name)
        state.results = [...state.results]
        state.results[ix] = {
          extractor: state.results[ix].extractor,
          response: { status: 'error', statusText: action.error.message || '' },
        }
      })
  },
})

// Export the actions and the reducer
export default searchSlice.reducer
export const { setActiveTab, updateQueryLinks } = searchSlice.actions
